import React, { use, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface';
import cn from 'clsx';
import styles from './styles.module.scss';
import { formFilters } from '@components-clean/core/api-grid/api-grid.helper';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { GroupedByMultiSelectOption, MultiSelectKeyValue, MultiSelectOption } from '@interfaces-clean/dropdowns';
import { EditReviewDto, SubjectMonitoringReviewDto } from '@interfaces-clean/reviews.interface';
import { AddSubjectReviewDto } from '@interfaces-clean/subject-monitoring/subject-review';
import { SMActiveReviewContext } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/active-review-tab/context';
import { getSessionStorageDatasetId } from '@contexts/local-state-context';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { ReviewPlanAPI } from '@apis/bff-api/study/review-plans';
import { SubjectReviewInstanceApi } from '@apis/bff-api/subject-monitoring/subject-review-instance';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type AddReviewDialogProps = {
    studyRef: string;
    onClose: () => void;
    subjects: { [key: string]: string };
    reviewAll: boolean;
    parentFilter?: React.RefObject<CompositeFilterDescriptor | null>;
    comparisonDataset?: number;
    totalRows: number;
    onSubmit: () => void;
    preselectedReview?: EditReviewDto | null;
};

interface INewReviewForm {
    types: MultiSelectOption | null;
    titles: MultiSelectOption | null;
    datasets: MultiSelectOption | null;
    reviewers: MultiSelectKeyValue[] | null;
}

const COMPONENT_NAME = 'subject-review-dialog';

const SubjectReviewDialog = ({
    studyRef,
    onClose,
    subjects,
    reviewAll,
    parentFilter,
    comparisonDataset,
    totalRows,
    onSubmit,
    preselectedReview,
}: AddReviewDialogProps) => {
    const datasetId = parseInt(getSessionStorageDatasetId() ?? '0', 10);
    const { t } = useTranslation();
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });
    const subjectCount = reviewAll ? totalRows : Object.keys(subjects).length;
    const subjectsPlural = `${subjectCount > 1 ? mtkTranslation('subjects') : mtkTranslation('subject')}`;
    const warningMessage = reviewAll
        ? mtkTranslation('addReviewsModal.addAllConfirmationMessageShort')
        : t('sm:monitoringToolkit.addReviewsModal.addReviewsMessage', {
              subjectCount,
              subjectsPlural,
          });
    const { sessionId } = useStudyDataFilterContext();
    const smActiveReviewContext = use(SMActiveReviewContext);

    // API Triggers
    const { data: reviewPlans, isLoading: isLoadingReviewTypes } = ReviewPlanAPI.useGetAll(studyRef ?? null);
    const { trigger: loadReviews, isMutating: isLoadingReviews } =
        SubjectMonitoringReviewAPI.useGetAllReviews(studyRef);
    const { trigger: addReviewInstance, isMutating: isSavingReviewInstances } =
        SubjectReviewInstanceApi.usePostReview(studyRef);

    const isProcessingData = isLoadingReviews || isLoadingReviewTypes || isSavingReviewInstances;

    // State variables
    const [reviewTypeOptions, setReviewTypeOptions] = useState<GroupedByMultiSelectOption[]>([]);
    const [reviews, setReviews] = useState<SubjectMonitoringReviewDto[]>([]);
    const [datasets, setDatasets] = useState<MultiSelectKeyValue[]>([]);
    const [titles, setTitles] = useState<MultiSelectKeyValue[]>([]);
    const [selectedReview, setSelectedReview] = useState<SubjectMonitoringReviewDto>();
    const [showReviewAllConfirmation, setShowReviewAllConfirmation] = useState(false);

    // Used for showing the no data warning. Checking the reviews and LOVs are not reliable enough
    // because they'll be empty before being loaded
    const [hasReviews, setHasReviews] = useState(true);
    const [hasReviewTypes, setHasReviewTypes] = useState(true);

    const disableTypes = useBoolean(false);
    const disableDatasets = useBoolean(true);
    const disableTitles = useBoolean(true);

    const { confirmDiscardChanges, setComponentHasChanges, componentHasChanges } = useUnsavedChangesContext();

    // Form handling
    const defaultValues = {
        types: null,
        titles: null,
        datasets: null,
        reviewers: null,
    };

    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid, isSubmitting },
        resetField,
        reset,
        watch,
        setValue,
    } = useForm<INewReviewForm>({
        mode: 'all',
        defaultValues,
    });

    // Subscribing to value changes in the form fields
    const watchTypes = watch('types');
    const watchDatasets = watch('datasets');
    const watchTitles = watch('titles');

    useEffect(() => {
        if (!preselectedReview || !reviewPlans) {
            return;
        }
        // Keep fields disabled, set as only options
        const plan = reviewPlans.find((x) => x.reviewTypeName === preselectedReview.type);
        if (plan) {
            const d: GroupedByMultiSelectOption = {
                id: plan.reviewTypeId!,
                value: plan.reviewTypeName!,
                active: true,
                groupedBy: 'Active',
            };
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setReviewTypeOptions([d]);
            setValue('types', { value: d.value, groupedBy: 'Active', id: d.id, active: true, disabled: false });
            disableTypes.setTrue();
        }

        const dataset: MultiSelectKeyValue = {
            id: String(preselectedReview.datasetId),
            value: preselectedReview.datasetDate!,
        };

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setDatasets([dataset]);
        setValue('datasets', {
            id: preselectedReview.datasetId!,
            groupedBy: '',
            active: true,
            value: preselectedReview.datasetDate!,
            disabled: false,
        });
        disableDatasets.setTrue();

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setTitles([{ id: String(preselectedReview.id!), value: preselectedReview.title! }]);
        setValue('titles', {
            id: preselectedReview.id!,
            value: preselectedReview.title!,
            disabled: false,
            active: true,
            groupedBy: 'Active',
        });
        disableTitles.setTrue();

        // This is not POSTed, so just show the name
        const reviewersKV: MultiSelectKeyValue[] =
            preselectedReview.reviewers?.map((x) => ({ id: x.name!, value: x.name! })) ?? [];

        setValue('reviewers', reviewersKV);

        // Ensure to keep changes as false, as there's nothing to select
        setComponentHasChanges(COMPONENT_NAME, false);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [preselectedReview, reviewPlans]);

    // Data Mapping & Triggering
    useEffect(() => {
        if (preselectedReview || !reviewPlans) {
            return;
        }

        // A review type is selectable when both its study Review Plan and the org-level
        // Review Type are active; the rest show under the disabled "Inactive" group.
        const data: GroupedByMultiSelectOption[] = reviewPlans
            .map((plan) => {
                const selectable = (plan.active ?? false) && (plan.reviewTypeActive ?? false);
                return {
                    id: plan.reviewTypeId!,
                    value: plan.reviewTypeName!,
                    active: selectable,
                    groupedBy: selectable ? 'Active' : 'Inactive',
                    disabled: !selectable,
                };
            })
            .sort((a, b) => {
                // Sort active items first (true values before false values)
                if (a.active !== b.active) {
                    return a.active ? -1 : 1;
                }

                return 0;
            });

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setReviewTypeOptions(data);
        setHasReviewTypes(data.length > 0);
        /* eslint-enable @eslint-react/set-state-in-effect */
    }, [preselectedReview, reviewPlans]);

    useEffect(() => {
        if (preselectedReview) {
            return;
        }
        loadReviews()
            .then((loadedData: SubjectMonitoringReviewDto[]) => {
                const isReviewed = loadedData.filter((x) => !x.reviewed);
                setReviews(isReviewed);
                setHasReviews(isReviewed.length > 0);
            })
            .catch((err) => window.console.log(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [loadReviews]);

    useEffect(() => {
        if (preselectedReview) {
            return;
        }
        if (!watchTypes || reviews.length === 0) return;
        const isReviewActive = reviewTypeOptions.find((x) => x.id === watchTypes.id)?.active;

        if (!isReviewActive) {
            disableDatasets.setTrue();
            return;
        }

        const datasetData: MultiSelectKeyValue[] = Array.from(
            new Map(
                reviews
                    .filter(
                        (review) =>
                            watchTypes?.value === review.type &&
                            (review.datasetId as number) >= datasetId &&
                            review.datasetIsActive === true, // Don't show any datasets that's aren't active
                    )
                    .map((review) => [
                        review.datasetDate,
                        { id: String(review.datasetId), value: review.datasetDate! } as MultiSelectKeyValue,
                    ]),
            ).values(),
        );

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setDatasets(datasetData);
        disableDatasets.setFalse();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watchTypes, reviews]);

    useEffect(() => {
        if (preselectedReview) {
            return;
        }
        if (!watchDatasets || !datasets) return;
        const selectedDatasetId = String(watchDatasets.id);

        const titlesData: MultiSelectKeyValue[] = reviews
            .filter((x) => String(x.datasetId) === selectedDatasetId && watchTypes?.value === x.type)
            .map((x, idx) => ({
                id: String(idx), // numeric id as a string
                value: x.title!, // your display value
            }));

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setTitles(titlesData);
        disableTitles.setFalse();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watchDatasets]);

    useEffect(() => {
        if (preselectedReview) {
            return;
        }
        if (!watchTypes || !watchDatasets || !watchTitles) return;

        const selectedDatasetId = String(watchDatasets.id);

        const reviewData = reviews.find(
            (x) =>
                x.type === watchTypes.value &&
                String(x.datasetId) === selectedDatasetId &&
                watchTitles.value === x.title,
        );

        if (!reviewData) return;

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedReview(reviewData);

        const reviewersArray = reviewData.reviewers
            ?.split(',') // Split by comma
            .map((r) => r.trim()) // Remove any extra spaces
            .filter((r) => r.length > 0); // Remove empty entries if any

        if (!reviewersArray) return;

        const reviewersKV: MultiSelectKeyValue[] = reviewersArray.map((r) => ({
            id: String(r), // use the reviewer as the id
            value: r, // and as the display value
        }));

        setValue('reviewers', reviewersKV);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watchTitles]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty]);

    useEffect(() => {
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const onSubmitHandler = async () =>
        handleSubmit(async () => {
            if (reviewAll && !showReviewAllConfirmation) {
                setShowReviewAllConfirmation(true);
                return;
            }

            if (reviewAll && showReviewAllConfirmation) {
                setShowReviewAllConfirmation(false);
            }

            const filtersData = parentFilter?.current ? formFilters(parentFilter?.current, 'datatable') : undefined;

            const reviewInstance: AddSubjectReviewDto = {
                reviewId: preselectedReview?.id ?? selectedReview?.id!,
                datasetId,
                subjects,
                reviewAll,
                sessionId,
                reviewModeSessionId: smActiveReviewContext?.state?.sessionId,
                filters: { filters: filtersData },
                comparisonDatasetId: comparisonDataset,
            };

            try {
                const result = await addReviewInstance(reviewInstance);
                if (result) {
                    ShowSuccess(t('actionsSuccessfulToast'));
                    onSubmit();
                } else {
                    ShowError(t('errorMessage'));
                }
            } catch {
                ShowError(t('errorMessage'));
                onClose();
            } finally {
                setComponentHasChanges(COMPONENT_NAME, false);
                onClose();
            }
        })();

    const onCloseHandler = async () => {
        if (showReviewAllConfirmation) {
            setComponentHasChanges(COMPONENT_NAME, false);
            onClose();
        } else {
            if (componentHasChanges(COMPONENT_NAME)) {
                const confirmed = await confirmDiscardChanges('add-review-dialog');
                if (!confirmed) {
                    return;
                }
                setComponentHasChanges(COMPONENT_NAME, false);
                reset(defaultValues);
            }
            onClose();
        }
    };

    const modalHeight = showReviewAllConfirmation ? 200 : !reviewTypeOptions.length ? 155 : 389;

    let title = '';
    if (reviewAll) {
        title = showReviewAllConfirmation
            ? mtkTranslation('addReviewsModal.reviewAllConfirmationTitle')
            : mtkTranslation('addReviewsModal.reviewAllSubjects');
    } else {
        title = mtkTranslation('addReviewsModal.addToReview');
    }

    const noReviewsOrLOVs = !hasReviews && !hasReviewTypes;

    let disablePrimaryButton;
    if (preselectedReview) {
        // isDirty check isn't required as fields are readonly
        disablePrimaryButton = !isValid || isSubmitting || isProcessingData;
    } else {
        disablePrimaryButton = !isDirty || !isValid || isSubmitting || isProcessingData;
    }

    return (
        <DraggableDialog
            dataTestId="add-subject-review-dialog"
            onClose={onCloseHandler}
            onSubmit={reviewTypeOptions.length ? onSubmitHandler : undefined}
            title={title}
            primaryBtnDisabled={disablePrimaryButton}
            primaryBtnLabel={showReviewAllConfirmation ? t('common:continue') : t('common:save')}
            height={modalHeight}
            width={412}
            secondaryBtnDisabled={isSubmitting || isProcessingData}
        >
            {showReviewAllConfirmation && (
                <div className="k-d-grid k-pb-2">
                    <div className={styles.confirmMessage}>
                        {t('sm:monitoringToolkit.addReviewsModal.addAllConfirmationMessage', {
                            subjectCount,
                            selectedReviewType: preselectedReview?.type ?? selectedReview?.type,
                            selectedReviewDatasetDate: preselectedReview?.datasetDate ?? selectedReview?.datasetDate,
                            selectedReviewTitle: preselectedReview?.title ?? selectedReview?.title,
                        })}
                    </div>
                </div>
            )}
            {!showReviewAllConfirmation && (
                <>
                    {noReviewsOrLOVs && (
                        <InfoField
                            message={mtkTranslation('addReviewsModal.noReviewTypes')}
                            layout="center"
                            type="Warning"
                        />
                    )}
                    {reviewTypeOptions.length > 0 && subjects && (
                        <div className={cn('k-w-full', 'k-h-full', 'k-d-flex-col')}>
                            <div className="k-d-grid k-pb-2">
                                <InfoField
                                    message={warningMessage}
                                    layout="stretch"
                                    type={reviewAll ? 'Warning' : 'Info'}
                                />
                            </div>
                            <BasicDropDownController
                                groupField="groupedBy"
                                activeField="active"
                                control={control}
                                name="types"
                                data={reviewTypeOptions}
                                data-testid="review-type"
                                label={mtkTranslation('addSubjectReview.typeLabel')}
                                textField="value"
                                dataItemKey="id"
                                filterable
                                allowClear
                                required
                                onChange={() => {
                                    disableDatasets.setTrue();
                                    disableTitles.setTrue();
                                    resetField('datasets');
                                    resetField('titles');
                                    resetField('reviewers');
                                }}
                                readOnly={disableTypes.value || isSubmitting || isProcessingData}
                            />
                            <BasicDropDownController
                                activeField="active"
                                control={control}
                                name="datasets"
                                data={datasets}
                                data-testid="new-review-type"
                                label={mtkTranslation('addSubjectReview.datasetLabel')}
                                textField="value"
                                dataItemKey="id"
                                filterable
                                allowClear
                                required
                                onChange={() => {
                                    resetField('titles');
                                    resetField('reviewers');
                                }}
                                readOnly={disableDatasets.value || isSubmitting || isProcessingData}
                            />
                            <BasicDropDownController
                                activeField="active"
                                control={control}
                                name="titles"
                                data={titles}
                                data-testid="new-review-type"
                                label={mtkTranslation('addSubjectReview.titleLabel')}
                                textField="value"
                                dataItemKey="id"
                                filterable
                                allowClear
                                required
                                onChange={() => {
                                    resetField('reviewers');
                                }}
                                readOnly={disableTitles.value || isSubmitting || isProcessingData}
                            />
                            <MultiSelectController
                                isEditing
                                control={control}
                                name="reviewers"
                                data-testid="review-lovs"
                                data={[]}
                                textField="value"
                                dataItemKey="id"
                                label={mtkTranslation('addSubjectReview.reviewer')}
                                disabled
                            />
                        </div>
                    )}
                </>
            )}
        </DraggableDialog>
    );
};

export default SubjectReviewDialog;
